export function isOpsFormValueFilled(value) {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (value === 0) {
    return true;
  }

  return value !== null && value !== undefined && String(value).trim() !== "";
}

export function isOpsFormPositiveValue(value) {
  return isOpsFormValueFilled(value) && Number(value) >= 1;
}

export function isOpsFormPhoneValue(value) {
  return /^[0-9]{5,15}$/.test(String(value || "").trim());
}

export function opsFormField(label, complete) {
  return {
    label,
    complete: Boolean(complete),
  };
}

export function normalizeOpsFormSections(sections) {
  return sections
    .filter((section) => section.visible !== false)
    .map((section) => {
      const fields = Array.isArray(section.fields) ? section.fields : [];
      const completedCount = fields.filter((field) => field.complete).length;
      const requiredCount = fields.length;

      return {
        ...section,
        fields,
        completedCount,
        requiredCount,
        optional: requiredCount === 0,
        complete: requiredCount > 0 && completedCount === requiredCount,
      };
    });
}

export function getOpsFormCompletedCount(sections) {
  return sections.reduce(
    (count, section) => count + section.completedCount,
    0
  );
}

export function getOpsFormRequiredCount(sections) {
  return sections.reduce((count, section) => count + section.requiredCount, 0);
}

export function getOpsFormCompletionPercent(sections) {
  const requiredCount = getOpsFormRequiredCount(sections);

  if (!requiredCount) {
    return 100;
  }

  return Math.round((getOpsFormCompletedCount(sections) / requiredCount) * 100);
}

export function mapOpsFormSectionsByKey(sections) {
  return sections.reduce((sectionMap, section) => {
    sectionMap[section.key] = section;
    return sectionMap;
  }, {});
}

function valueField(label, value) {
  return opsFormField(label, isOpsFormValueFilled(value));
}

function positiveField(label, value) {
  return opsFormField(label, isOpsFormPositiveValue(value));
}

function phoneField(label, value) {
  return opsFormField(label, isOpsFormPhoneValue(value));
}

function getFormShipments(vm) {
  return vm.x && Array.isArray(vm.x.sub) && vm.x.sub.length
    ? vm.x.sub
    : [{}];
}

function fieldLabel(label, index, total) {
  return total > 1 ? `Shipment ${index + 1} ${label}` : label;
}

function isPkr(vm) {
  return vm.x && vm.x.customerCurrency == "PKR";
}

function needsLoadType(vm, shipment) {
  return (
    isPkr(vm) &&
    shipment &&
    shipment.jobGeoType == "2" &&
    shipment.jobMode == "3"
  );
}

function needsArabicRoute(vm) {
  return (
    vm.x &&
    (vm.x.invoiceBy == 1 || vm.x.customerCurrency == "SAR")
  );
}

function routeCityComplete(value, errorState) {
  if (errorState === undefined) {
    return isOpsFormValueFilled(value);
  }

  return isOpsFormValueFilled(value) && errorState === false;
}

function addShipmentPlanFields(fields, vm, shipment, index, total, options = {}) {
  const shipmentPrice = options.priceKey === "jobPrice"
    ? shipment.jobPrice || shipment.assignmentjobPrice
    : shipment.quotedPrice || shipment.quotedPricePerAssignment;
  const priceLabel = options.priceLabel || "Target Price";

  fields.push(
    valueField(fieldLabel("Job Division", index, total), shipment.jobDivison),
    valueField(fieldLabel("Job Type", index, total), shipment.jobType),
    valueField(fieldLabel("Job Booking", index, total), shipment.jobBooking),
    valueField(fieldLabel("Route Type", index, total), shipment.jobGeoType),
    valueField(fieldLabel("Shipment Mode", index, total), shipment.jobMode)
  );

  if (needsLoadType(vm, shipment)) {
    fields.push(valueField(fieldLabel("Load Type", index, total), shipment.loadType));
  }

  fields.push(
    valueField(fieldLabel("Price Type", index, total), shipment.priceType),
    positiveField(fieldLabel(priceLabel, index, total), shipmentPrice),
    valueField(fieldLabel("Truck Category", index, total), shipment.truckTypeParent),
    valueField(fieldLabel("Truck Type", index, total), shipment.truckType),
    positiveField(
      fieldLabel("Assignments Required", index, total),
      shipment.numberOfAssignmentsRequired
    ),
    valueField(fieldLabel("Job Start Date", index, total), shipment.startDateSelect),
    valueField(fieldLabel("Pickup Time", index, total), shipment.pickupTime),
    valueField(fieldLabel("Pickup Location", index, total), shipment.sourceLocation),
    opsFormField(
      fieldLabel("Pickup City", index, total),
      routeCityComplete(shipment.sourceCity, shipment.sourceCityError)
    ),
    valueField(fieldLabel("Pickup Country", index, total), shipment.sourceCountry),
    valueField(fieldLabel("Pickup Address", index, total), shipment.sourceAddress),
    valueField(fieldLabel("Drop Location", index, total), shipment.destinationLocation),
    opsFormField(
      fieldLabel("Drop City", index, total),
      routeCityComplete(shipment.destinationCity, shipment.destinationCityError)
    ),
    valueField(fieldLabel("Drop Country", index, total), shipment.destinationCountry),
    valueField(fieldLabel("Drop Address", index, total), shipment.destinationAddress)
  );

  if (needsArabicRoute(vm)) {
    fields.push(
      valueField(
        fieldLabel("Pickup City Arabic", index, total),
        shipment.arabicsourceCity || shipment.sourceCityArabic
      ),
      valueField(
        fieldLabel("Pickup Country Arabic", index, total),
        shipment.arabicsourceCountry || shipment.sourceCountryArabic
      ),
      valueField(
        fieldLabel("Drop City Arabic", index, total),
        shipment.arabicdestinationCity || shipment.destinationCityArabic
      ),
      valueField(
        fieldLabel("Drop Country Arabic", index, total),
        shipment.arabicdestinationCountry || shipment.destinationCountryArabic
      )
    );
  }
}

export function buildInquiryProgressSections(vm, options = {}) {
  const shipments = getFormShipments(vm);
  const shipmentFields = [];
  const requireAdditional = options.requireAdditional !== false;
  const sectionIds = options.sectionIds || {};
  const additionalFields = [];

  shipments.forEach((shipment, index) => {
    addShipmentPlanFields(shipmentFields, vm, shipment, index, shipments.length);
  });

  if (requireAdditional) {
    additionalFields.push(
      valueField("Inquiry Source", vm.x && vm.x.inquirySource),
      valueField("Shipment Type", vm.x && vm.x.shipmentType),
      valueField("Quote Needed Date", vm.quoteDate),
      valueField("Quote Needed Hour", vm.quoteTime && vm.quoteTime.HH),
      valueField("Quote Needed Minute", vm.quoteTime && vm.quoteTime.mm)
    );

    if (isPkr(vm)) {
      additionalFields.push(
        valueField("Contact Person Name", vm.x && vm.x.contactPersonName),
        valueField("Contact Address", vm.x && vm.x.contactAddress)
      );
    }
  }

  return normalizeOpsFormSections([
    {
      key: "customer",
      id: sectionIds.customer,
      label: "Customer Details",
      fields: [
        valueField(
          "Company Name",
          vm.x && (vm.x.customerId || vm.customerId || vm.ownerName)
        ),
        valueField("Billing Address", vm.x && vm.x.customerAddress),
        ...(isPkr(vm) ? [] : [valueField("VAT Number", vm.x && vm.x.vatNumber)]),
        valueField("Customer Currency", vm.x && vm.x.customerCurrency),
        valueField("Industry", vm.x && vm.x.selectedIndustry),
        valueField("Packing Type", vm.x && vm.x.packingType),
        valueField("Commodity", vm.x && vm.x.commodity),
        valueField("Invoice Country", vm.x && vm.x.invoiceBy),
        valueField("Invoice City", vm.x && vm.x.invoicingCity),
      ],
    },
    {
      key: "shipment",
      id: sectionIds.shipment,
      label: "Shipment Plan",
      fields: shipmentFields,
    },
    {
      key: "additional",
      id: sectionIds.additional,
      label: "Additional Details",
      fields: additionalFields,
    },
    {
      key: "documents",
      id: sectionIds.documents,
      label: "Documents",
      fields: [valueField("Proof", vm.x && vm.x.proof)],
    },
  ]);
}

export function buildJobProgressSections(vm, options = {}) {
  const shipments = getFormShipments(vm);
  const shipmentFields = [];
  const routeAndCommercialFields = [];
  const sectionIds = options.sectionIds || {};

  shipments.forEach((shipment, index) => {
    shipmentFields.push(
      valueField(fieldLabel("Job Division", index, shipments.length), shipment.jobDivison),
      valueField(fieldLabel("Job Type", index, shipments.length), shipment.jobType),
      valueField(fieldLabel("Job Booking", index, shipments.length), shipment.jobBooking),
      valueField(fieldLabel("Route Type", index, shipments.length), shipment.jobGeoType),
      valueField(fieldLabel("Shipment Mode", index, shipments.length), shipment.jobMode)
    );

    if (needsLoadType(vm, shipment)) {
      shipmentFields.push(
        valueField(fieldLabel("Load Type", index, shipments.length), shipment.loadType)
      );
    }

    routeAndCommercialFields.push(
      valueField(fieldLabel("Price Type", index, shipments.length), shipment.priceType),
      positiveField(
        fieldLabel("Customer Rate", index, shipments.length),
        shipment.jobPrice || shipment.assignmentjobPrice
      ),
      valueField(
        fieldLabel("Truck Category", index, shipments.length),
        shipment.truckTypeParent
      ),
      valueField(fieldLabel("Truck Type", index, shipments.length), shipment.truckType),
      positiveField(
        fieldLabel("Assignments Required", index, shipments.length),
        shipment.numberOfAssignmentsRequired
      ),
      valueField(fieldLabel("Job Start Date", index, shipments.length), shipment.startDateSelect),
      valueField(fieldLabel("Pickup Time", index, shipments.length), shipment.pickupTime),
      valueField(fieldLabel("Pickup Location", index, shipments.length), shipment.sourceLocation),
      opsFormField(
        fieldLabel("Pickup City", index, shipments.length),
        routeCityComplete(shipment.sourceCity, shipment.sourceCityError)
      ),
      valueField(fieldLabel("Pickup Country", index, shipments.length), shipment.sourceCountry),
      valueField(fieldLabel("Pickup Address", index, shipments.length), shipment.sourceAddress),
      valueField(fieldLabel("Drop Location", index, shipments.length), shipment.destinationLocation),
      opsFormField(
        fieldLabel("Drop City", index, shipments.length),
        routeCityComplete(shipment.destinationCity, shipment.destinationCityError)
      ),
      valueField(fieldLabel("Drop Country", index, shipments.length), shipment.destinationCountry),
      valueField(fieldLabel("Drop Address", index, shipments.length), shipment.destinationAddress)
    );

    if (needsArabicRoute(vm)) {
      routeAndCommercialFields.push(
        valueField(
          fieldLabel("Pickup City Arabic", index, shipments.length),
          shipment.arabicsourceCity || shipment.sourceCityArabic
        ),
        valueField(
          fieldLabel("Pickup Country Arabic", index, shipments.length),
          shipment.arabicsourceCountry || shipment.sourceCountryArabic
        ),
        valueField(
          fieldLabel("Drop City Arabic", index, shipments.length),
          shipment.arabicdestinationCity || shipment.destinationCityArabic
        ),
        valueField(
          fieldLabel("Drop Country Arabic", index, shipments.length),
          shipment.arabicdestinationCountry || shipment.destinationCountryArabic
        )
      );
    }
  });

  return normalizeOpsFormSections([
    {
      key: "customer",
      id: sectionIds.customer,
      label: "Customer Details",
      fields: [
        valueField(
          "Company Name",
          vm.x && (vm.x.customerId || vm.customerId || vm.ownerName)
        ),
        valueField("Billing Address", vm.x && vm.x.customerAddress),
        ...(isPkr(vm) ? [] : [valueField("VAT Number", vm.x && vm.x.vatNumber)]),
        valueField("Customer Currency", vm.x && vm.x.customerCurrency),
        valueField("Industry", vm.x && vm.x.selectedIndustry),
        valueField("Packing Type", vm.x && vm.x.packingType),
        valueField("Invoice Country", vm.x && vm.x.invoiceBy),
        valueField("Invoice City", vm.x && vm.x.invoicingCity),
      ],
    },
    {
      key: "shipment",
      id: sectionIds.shipment,
      label: "Shipment Plan",
      fields: shipmentFields,
    },
    {
      key: "commercials",
      id: sectionIds.commercials,
      label: "Carriage & Equipment",
      fields: routeAndCommercialFields,
    },
    {
      key: "documents",
      id: sectionIds.documents,
      label: "Documents",
      fields: [valueField("Proof", vm.x && vm.x.proof)],
    },
  ]);
}

export function buildCustomerProgressSections(vm, options = {}) {
  const sectionIds = options.sectionIds || {};
  const isEditForm = options.isEdit === true;
  const isPakCustomer = vm.invoicingCountry === "PAK";
  const isKsaCustomer = vm.invoicingCountry === "KSA";
  const customerPhone = vm.phoneNumber || vm.phoneNo;
  const customerEmail = vm.emailId || vm.email;
  const customerLicenseNumber =
    vm.licenseNumber || vm.companyLicenseNumber;
  const customerOwnerPhone = vm.phoneNumber2 || vm.phoneNo2;

  // The "Documentation" block on the form only holds Internal Approval (required)
  // and Miscellaneous Approval (optional). Every identity / licence document
  // is rendered inside "Owner Details", so it is counted there to match the UI.
  const documentationFields = [
    valueField("Internal Approval", vm.internalApproval),
  ];

  const ownerDocumentFields = [];

  if (!isPakCustomer) {
    ownerDocumentFields.push(
      valueField("Passport", vm.passPort),
      valueField("Passport Expiry", vm.passportExpiryDate),
      valueField("Visa", vm.visa),
      valueField("Visa Expiry", vm.visaExpiryDate),
      valueField(isKsaCustomer ? "Oman ID" : "Emirates ID", vm.emiratesId),
      valueField(isKsaCustomer ? "Oman ID Expiry" : "Emirates ID Expiry", vm.emirateExpiryDate),
      valueField("VAT Certificate", vm.vatCertificate)
    );
  }

  ownerDocumentFields.push(
    valueField(
      isPakCustomer ? "STRN Certificate Expiry" : "VAT Certificate Expiry",
      vm.vatCertificateExpiryDate
    ),
    valueField(
      isPakCustomer ? "NTN Certificate" : "Trade License",
      vm.tradeLicense
    ),
    valueField(
      isPakCustomer ? "NTN Certificate Expiry" : "Trade License Expiry",
      vm.tradeLicenseExpiryDate
    )
  );

  return normalizeOpsFormSections([
    {
      key: "company",
      id: sectionIds.company,
      label: "Company Details",
      fields: [
        valueField("Company Name", vm.companyName),
        ...(isKsaCustomer ? [valueField("Company Name Arabic", vm.arabiccompanyName)] : []),
        valueField("Invoice Country", vm.invoicingCountry),
        valueField("Invoice City", vm.invoicingCity),
        valueField("Country Code", vm.countryCode),
        phoneField("Phone Number", customerPhone),
        ...(isPakCustomer ? [] : [valueField("VAT Number", vm.companyVatNumber)]),
        valueField(
          isPakCustomer ? "NTN Number" : "Company License Number",
          customerLicenseNumber
        ),
        valueField("Customer Address", vm.address),
        valueField("Email Id", customerEmail),
        ...(!isEditForm
          ? [
              valueField("Password", vm.password),
              valueField("Repeat Password", vm.repeatPassword),
            ]
          : []),
        valueField("License Expiry Date", vm.licenseDate),
        valueField("Industry", vm.selectedIndustry),
        valueField("Credit Duration", vm.invoiceDay),
        // valueField("Credit Limit", vm.creditLimit),
        ...(isKsaCustomer
          ? [
              valueField("Building Number", vm.buildingNumber),
              valueField("Postal Code", vm.postalCode),
              valueField("Address Line 1 Arabic", vm.arabicaddress1),
              valueField("City Arabic", vm.arabiccity1),
            ]
          : []),
        valueField("Address Line 1", vm.address1),
        opsFormField(
          "City",
          isOpsFormValueFilled(vm.city1) && vm.city1Error === false
        ),
      ],
    },
    {
      key: "documentation",
      id: sectionIds.documentation,
      label: "Documentation",
      fields: documentationFields,
    },
    {
      key: "owner",
      id: sectionIds.owner,
      label: "Owner Details",
      fields: [
        valueField("Owner / Contact Person", vm.ownerName || vm.name),
        ...(isPakCustomer
          ? [
              valueField("Owner Country Code", vm.countryCode2),
              phoneField("Owner Phone Number", customerOwnerPhone),
            ]
          : []),
        ...ownerDocumentFields,
      ],
    },
    {
      key: "contacts",
      id: sectionIds.contacts,
      label: "Other Contacts",
      fields: [valueField("Contact", vm.contactArr)],
    },
  ]);
}

export function buildTransporterProgressSections(vm, options = {}) {
  const sectionIds = options.sectionIds || {};
  const transporterCountry = vm.countrySortNames || vm.countrySortName;
  const isPakTransporter = transporterCountry === "PAK";
  const documentationFields = [];

  if (isPakTransporter) {
    documentationFields.push(valueField("NTN Number", vm.NTNNumber));

    documentationFields.push(
      valueField("NTN Certificate", vm.tradeLicense),
      valueField("CNIC Front", vm.CNICFront),
      valueField("CNIC Back", vm.CNICBack)
    );
  } else {
    documentationFields.push(
      valueField("Trade License Number", vm.tradeLicenseNumber),
      valueField("Passport Number", vm.passport)
    );

    documentationFields.push(
      valueField("Trade License", vm.tradeLicense),
      valueField("VAT Certificate", vm.vatCertificate),
      valueField("Passport Front", vm.passportFront),
      valueField("Passport Back", vm.passportBack)
    );
  }

  return normalizeOpsFormSections([
    {
      key: "company",
      id: sectionIds.company,
      label: "Company Details",
      fields: [
        valueField("Registering As", vm.type),
        valueField("Country", transporterCountry),
        valueField("Transporter Type", vm.UserType),
        valueField("Company Name", vm.companyName),
        valueField("Username", vm.username),
        valueField("Email Id", vm.emailId),
        valueField("Password", vm.password),
      ],
    },
    {
      key: "documentation",
      id: sectionIds.documentation,
      label: "Documentation",
      fields: documentationFields,
    },
    {
      key: "contact",
      id: sectionIds.contact,
      label: "Contact Details",
      fields: [
        valueField("Country Code", vm.countryCode),
        phoneField("Phone Number", vm.phoneNo),
        ...(isPakTransporter
          ? [valueField("Contact Person Name", vm.ContactPersonName)]
          : []),
      ],
    },
  ]);
}
